'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

type DeferredTargetResolver = (host: HTMLElement) => Element | null;

export function useDeferredClientComponent<Component>(
  loadComponent: () => Promise<Component>,
  {
    enabled = true,
    resolveTarget,
    rootMargin = '1200px 0px',
    targetSelector,
  }: {
    enabled?: boolean;
    resolveTarget?: DeferredTargetResolver;
    rootMargin?: string;
    targetSelector?: string;
  } = {}
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const loadPromiseRef = useRef<Promise<Component> | null>(null);
  const [Component, setComponent] = useState<Component | null>(null);

  const load = useCallback(() => {
    if (!enabled || Component || loadPromiseRef.current) {
      return;
    }

    loadPromiseRef.current = loadComponent()
      .then((resolvedComponent) => {
        setComponent(() => resolvedComponent);
        return resolvedComponent;
      })
      .catch((error) => {
        loadPromiseRef.current = null;

        if (process.env.NODE_ENV !== 'production') {
          console.error('[cms-deferred-client-component] load failed', error);
        }

        throw error;
      });
  }, [Component, enabled, loadComponent]);

  useEffect(() => {
    if (!enabled || Component) {
      return;
    }

    const host = hostRef.current;
    const target = host && resolveTarget ? resolveTarget(host) : null;
    const observerTarget = target
      ? target
      : targetSelector
        ? document.querySelector<HTMLElement>(targetSelector)
        : host;

    if (!observerTarget || !('IntersectionObserver' in window)) {
      load();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        load();
        observer.disconnect();
      },
      { rootMargin }
    );

    observer.observe(observerTarget);
    return () => {
      observer.disconnect();
    };
  }, [Component, enabled, load, resolveTarget, rootMargin, targetSelector]);

  return {
    Component,
    hostRef,
    load,
  };
}
